import { DBSchema, openDB } from 'idb';
import { Item } from '../../domain/item';
import { Location } from '../../domain/location';
import { clearImageBlobCaches } from '../images/imageBlobCache';

export interface CachedInventory {
  identity: string;
  items?: { records: Item[]; completedAt: number };
  locations?: { records: Location[]; completedAt: number };
}

export interface InventoryMetadataCache {
  read(collectionId: string): Promise<CachedInventory | undefined>;
  write(collectionId: string, value: CachedInventory): Promise<void>;
  remove(collectionId: string): Promise<void>;
  clear(): Promise<void>;
}

interface InventoryCacheSchema extends DBSchema {
  inventory: {
    key: [string, string];
    value: CachedInventory;
  };
}

const DATABASE_NAME = 'qkindex-inventory-metadata-v1';
let pending: Promise<unknown> = Promise.resolve();
let generation = 0;
const userGenerations = new Map<string, number>();

// Serialize all operations, including across factories. A sign-out clear cannot
// be overtaken by a write that was already waiting for IndexedDB to open.
const enqueue = <T>(operation: () => Promise<T>): Promise<T | undefined> => {
  const result = pending.then(operation).catch(() => undefined);
  pending = result;
  return result;
};

const withDatabase = async <T>(
  operation: (
    db: Awaited<ReturnType<typeof openInventoryDatabase>>,
  ) => Promise<T>,
): Promise<T> => {
  const db = await openInventoryDatabase();
  try {
    return await operation(db);
  } finally {
    db.close();
  }
};

const openInventoryDatabase = () =>
  openDB<InventoryCacheSchema>(DATABASE_NAME, 2, {
    upgrade(db) {
      if (db.objectStoreNames.contains('inventory'))
        db.deleteObjectStore('inventory');
      db.createObjectStore('inventory');
    },
  });

const metadataOnly = (value: CachedInventory): CachedInventory => ({
  identity: value.identity,
  ...(value.items && {
    items: {
      completedAt: value.items.completedAt,
      records: value.items.records.map(
        ({ image, imageLoading, requestImage, ...item }) => ({
          ...item,
          image: /^(?:blob|data):/i.test(image.trim()) ? '' : image,
        }),
      ),
    },
  }),
  ...(value.locations && {
    locations: {
      completedAt: value.locations.completedAt,
      records: value.locations.records.map((location) => ({ ...location })),
    },
  }),
});

/** Best-effort metadata storage. Callers must validate server access before read. */
export const createInventoryMetadataCache = (
  userId: string,
): InventoryMetadataCache => {
  const sessionGeneration = generation;
  const userGeneration = userGenerations.get(userId) ?? 0;
  const current = () =>
    sessionGeneration === generation &&
    userGeneration === (userGenerations.get(userId) ?? 0);

  return {
    async read(collectionId) {
      return enqueue(async () => {
        if (!current()) return undefined;
        const value = await withDatabase((db) =>
          db.get('inventory', [userId, collectionId]),
        );
        return current() ? value : undefined;
      });
    },
    async write(collectionId, value) {
      // Snapshot before enqueueing so subsequent UI mutations cannot change the
      // pending write, and temporary image URLs and display callbacks never enter persistent storage.
      const snapshot = metadataOnly(value);
      await enqueue(async () => {
        if (!current()) return;
        await withDatabase((db) =>
          db.put('inventory', snapshot, [userId, collectionId]),
        );
      });
    },
    async remove(collectionId) {
      if (!current()) return;
      void clearImageBlobCaches(userId, collectionId);
      await enqueue(async () => {
        if (!current()) return;
        await withDatabase((db) =>
          db.delete('inventory', [userId, collectionId]),
        );
      });
    },
    async clear() {
      if (!current()) return;
      void clearImageBlobCaches(userId);
      userGenerations.set(userId, userGeneration + 1);
      await enqueue(() =>
        withDatabase(async (db) => {
          const tx = db.transaction('inventory', 'readwrite');
          let cursor = await tx.store.openCursor();
          while (cursor) {
            if (cursor.key[0] === userId) await cursor.delete();
            cursor = await cursor.continue();
          }
          await tx.done;
        }),
      );
    },
  };
};

/** Invalidates existing factories, including late writes from departed sessions. */
export const clearInventoryMetadataCaches = async (): Promise<void> => {
  const imagesCleared = clearImageBlobCaches();
  generation += 1;
  userGenerations.clear();
  await enqueue(() => withDatabase((db) => db.clear('inventory')));
  await imagesCleared;
};
